import {
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent,
  type ReactNode,
} from 'react'

import { formatChange } from '@/components/charts/treemap-bands'
import { squarify, type Rect } from '@/components/charts/treemap-layout'
import type { MenuOption } from '@/components/ui/MenuItem'
import MenuSingle from '@/components/ui/MenuSingle'
import type { SegmentOption } from '@/components/ui/SegmentedControl'
import SelectTrigger from '@/components/ui/SelectTrigger'
import StockAvatar from '@/components/ui/StockAvatar'
import MetricFigure from '@/features/screener/components/MetricFigure'
import SegmentToggle from '@/features/screener/components/SegmentToggle'
import SymbolTarget from '@/features/screener/components/SymbolTarget'
import { toChartNumber } from '@/lib/money'
import { cn } from '@/lib/utils'
import type { Metric, ScreenerRow } from '@/schemas/screener'

/**
 * What a tile's area can be: the mock's Market cap and Volume. Its Turnover has no screener
 * metric behind it, so it is left out.
 */
type SizeKey = 'market_cap' | 'volume'

const SIZE_OPTIONS: readonly SegmentOption<SizeKey>[] = [
  { value: 'market_cap', label: 'Market cap' },
  { value: 'volume', label: 'Volume' },
]

/**
 * What a tile's colour can be: the moves among the mock's "Change today / 1 week / 1 month / 3
 * months / 1 year" that a screener metric serves. A month and three months have none.
 */
const COLOUR_KEYS = ['change_percent', 'performance_1w', 'market_cap_change_1y'] as const

/**
 * How big a move takes the full colour, per metric (the mock's: 4% for a day or a week, 12% for a
 * year). A move smaller than `FLAT` is the inset grey.
 */
const FULL_AT: Readonly<Record<string, number>> = {
  change_percent: 4,
  performance_1w: 4,
  market_cap_change_1y: 12,
}

const FLAT = 0.05

/** The mock's map heights: taller on a narrow box, so the tiles stay near square. */
const NARROW_BELOW = 560
const NARROW_HEIGHT = 640
const WIDE_HEIGHT = 520

/** A sector's name line, drawn when its box has room for it (the mock's 56 × 90). */
const HEAD_HEIGHT = 20
const HEAD_MIN = { w: 90, h: 56 }

/** Under this an area would vanish; a stock with no figure still sits in its sector. */
const MIN_WEIGHT = 1e-4

/**
 * The tile's fill: the move's direction colour mixed into the inset grey, from 22% at the faintest
 * move to 92% at `FULL_AT` and beyond (the mock's scale), so a strong day and a quiet one are
 * told apart without a legend of steps. Token colours only, mixed in the browser.
 */
const fillFor = (move: number | null, fullAt: number): string => {
  if (move === null || Math.abs(move) < FLAT) return 'var(--muted)'
  const share = Math.round(22 + Math.min(1, Math.abs(move) / fullAt) * 70)
  const tone = move > 0 ? 'var(--market-up-fill)' : 'var(--market-down-fill)'
  return `color-mix(in srgb, ${tone} ${String(share)}%, var(--muted))`
}

/** Areas scaled to fill `box`, then squarified (rows along the shorter side). */
const layoutIn = (weights: readonly number[], box: Rect): Rect[] => {
  const total = weights.reduce((sum, weight) => sum + weight, 0)
  const scale = total > 0 ? (box.w * box.h) / total : 0
  return squarify(
    weights.map((weight) => weight * scale),
    box,
  )
}

interface Tile {
  readonly row: ScreenerRow
  readonly rect: Rect
  readonly move: number | null
}

interface SectorBox {
  readonly name: string
  readonly rect: Rect
  readonly head: boolean
  /** The sector's move, weighted by the size metric. */
  readonly move: number | null
  readonly tiles: readonly Tile[]
}

const figure = (row: ScreenerRow, key: string): number | null => {
  const value = row.values[key] ?? null
  return value === null ? null : toChartNumber(value)
}

function layoutMap(
  rows: readonly ScreenerRow[],
  sizeKey: SizeKey,
  colourKey: string,
  width: number,
  height: number,
): SectorBox[] {
  const weight = (row: ScreenerRow) => Math.max(MIN_WEIGHT, figure(row, sizeKey) ?? 0)
  const bySector = new Map<string, ScreenerRow[]>()
  for (const row of rows) {
    const name = row.sectorName ?? 'Other'
    bySector.set(name, [...(bySector.get(name) ?? []), row])
  }
  const sectors = [...bySector.entries()]
    .map(([name, members]) => {
      const items = [...members].sort((a, b) => weight(b) - weight(a))
      const sum = items.reduce((total, row) => total + weight(row), 0)
      const moved = items.filter((row) => figure(row, colourKey) !== null)
      const movedWeight = moved.reduce((total, row) => total + weight(row), 0)
      const move =
        movedWeight > 0
          ? moved.reduce((total, row) => total + (figure(row, colourKey) ?? 0) * weight(row), 0) /
            movedWeight
          : null
      return { name, items, sum, move }
    })
    .sort((a, b) => b.sum - a.sum)

  const boxes = layoutIn(
    sectors.map((sector) => sector.sum),
    { x: 0, y: 0, w: width, h: height },
  )
  return sectors.map((sector, index) => {
    const rect = boxes[index] ?? { x: 0, y: 0, w: 0, h: 0 }
    const head = rect.h > HEAD_MIN.h && rect.w > HEAD_MIN.w
    const inner = {
      x: rect.x + 1,
      y: rect.y + (head ? HEAD_HEIGHT : 0) + 1,
      w: Math.max(0, rect.w - 2),
      h: Math.max(0, rect.h - (head ? HEAD_HEIGHT : 0) - 2),
    }
    const rects = layoutIn(sector.items.map(weight), inner)
    return {
      name: sector.name,
      rect,
      head,
      move: sector.move,
      tiles: sector.items.map((row, at) => ({
        row,
        rect: rects[at] ?? { x: 0, y: 0, w: 0, h: 0 },
        move: figure(row, colourKey),
      })),
    }
  })
}

/** The box's own width, measured, so the map is laid out for the box it is painted in. */
function useWidth<T extends HTMLElement>() {
  const ref = useRef<T>(null)
  const [width, setWidth] = useState(0)
  useLayoutEffect(() => {
    const node = ref.current
    if (node === null) return
    const measure = () => {
      setWidth(node.clientWidth)
    }
    measure()
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure)
    observer?.observe(node)
    return () => {
      observer?.disconnect()
    }
  }, [])
  return { ref, width }
}

const box = (rect: Rect, inset = 0): CSSProperties => ({
  left: rect.x + inset,
  top: rect.y + inset,
  width: Math.max(0, rect.w - inset * 2),
  height: Math.max(0, rect.h - inset * 2),
})

interface Hover {
  readonly row: ScreenerRow
  readonly x: number
  readonly y: number
}

interface ScreenerHeatmapProps {
  /** Every matching stock, not one page. */
  rows: readonly ScreenerRow[]
  metrics: readonly Metric[]
  /** The view menu, at the right end of the bar as on every view. */
  viewMenu: ReactNode
  onOpenSymbol?: ((symbol: string) => void) | undefined
}

/**
 * Heatmap (the mock's `Gpe`): every matching stock as a tile inside its sector's box, the area
 * its market cap or volume and the colour its move. The sectors are laid out by their total and
 * headed with their weighted move where the box has room; a tile prints its ticker and move once
 * it is big enough to read. Hovering a tile shows its price, move and market cap; each tile is a
 * link to the stock.
 */
export default function ScreenerHeatmap({
  rows,
  metrics,
  viewMenu,
  onOpenSymbol,
}: ScreenerHeatmapProps) {
  const colourMetrics = COLOUR_KEYS.flatMap((key) => {
    const metric = metrics.find((candidate) => candidate.key === key)
    return metric === undefined ? [] : [metric]
  })
  const [sizeKey, setSizeKey] = useState<SizeKey>('market_cap')
  const [colourKey, setColourKey] = useState<string>(colourMetrics[0]?.key ?? 'change_percent')
  const [colourOpen, setColourOpen] = useState(false)
  const [hover, setHover] = useState<Hover | null>(null)
  const { ref, width } = useWidth<HTMLDivElement>()
  const height = width > 0 && width < NARROW_BELOW ? NARROW_HEIGHT : WIDE_HEIGHT

  const colourMetric = colourMetrics.find((metric) => metric.key === colourKey)
  const price = metrics.find((metric) => metric.key === 'ltp')
  const cap = metrics.find((metric) => metric.key === 'market_cap')
  const fullAt = FULL_AT[colourKey] ?? 4

  const sectors = useMemo(
    () => (width > 0 ? layoutMap(rows, sizeKey, colourKey, width, height) : []),
    [rows, sizeKey, colourKey, width, height],
  )

  const colourOptions: readonly MenuOption[] = colourMetrics.map((metric) => ({
    value: metric.key,
    label: metric.label,
  }))

  const track = (row: ScreenerRow) => (event: PointerEvent<HTMLElement>) => {
    const bounds = ref.current?.getBoundingClientRect()
    if (bounds === undefined) return
    setHover({ row, x: event.clientX - bounds.left, y: event.clientY - bounds.top })
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <span className="inline-flex items-center gap-2">
          <span className="text-body-xs text-muted-foreground-subtle">Size by</span>
          <SegmentToggle
            options={SIZE_OPTIONS}
            value={sizeKey}
            onChange={setSizeKey}
            ariaLabel="Size by"
          />
        </span>
        <span className="inline-flex items-center gap-2">
          <span className="text-body-xs text-muted-foreground-subtle">Colour by</span>
          <MenuSingle
            options={colourOptions}
            value={colourKey}
            onChange={setColourKey}
            open={colourOpen}
            onOpenChange={setColourOpen}
            label="Colour by"
            searchable={false}
            align="end"
            className="w-60"
            trigger={(triggerProps) => (
              <SelectTrigger
                {...triggerProps}
                aria-label={`Colour by, ${colourMetric?.label ?? ''}`}
                label={colourMetric?.label ?? ''}
                open={colourOpen}
              />
            )}
          />
          {viewMenu}
        </span>
      </div>

      <div
        ref={ref}
        aria-label="Heatmap of the matching stocks"
        role="group"
        className="relative overflow-hidden bg-card"
        style={{ height }}
        onPointerLeave={() => {
          setHover(null)
        }}
      >
        {sectors.map((sector) => (
          <div
            key={sector.name}
            aria-hidden="true"
            className="absolute shadow-[inset_0_0_0_1px_var(--background)]"
            style={box(sector.rect)}
          >
            {sector.head ? (
              <p className="absolute inset-x-2 top-0 truncate leading-5 text-body-xs text-muted-foreground">
                <span className="font-semibold">{sector.name}</span>{' '}
                <span
                  className={cn(
                    sector.move === null
                      ? null
                      : sector.move >= 0
                        ? 'text-market-up'
                        : 'text-market-down',
                  )}
                >
                  {sector.move === null ? '' : formatChange(sector.move)}
                </span>
              </p>
            ) : null}
          </div>
        ))}
        {sectors.flatMap((sector) =>
          sector.tiles
            .filter((tile) => tile.rect.w >= 2 && tile.rect.h >= 2)
            .map(({ row, rect, move }) => {
              const roomy = rect.w > 84 && rect.h > 64
              const labelled = rect.w > 46 && rect.h > 34
              return (
                <SymbolTarget
                  key={row.symbol}
                  symbol={row.symbol}
                  onOpenSymbol={onOpenSymbol}
                  aria-label={`${row.symbol}, ${move === null ? 'no figure' : formatChange(move)}`}
                  onPointerMove={track(row)}
                  onFocus={() => {
                    setHover(null)
                  }}
                  className={cn(
                    'absolute flex cursor-pointer flex-col items-center justify-center gap-1 overflow-hidden text-center leading-tight text-foreground',
                    'transition-[filter] duration-fast hover:z-10 hover:brightness-112 hover:outline-1 hover:-outline-offset-1 hover:outline-foreground',
                    'focus-visible:z-10 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring',
                  )}
                  style={{ ...box(rect, 1), background: fillFor(move, fullAt) }}
                >
                  {roomy ? (
                    <StockAvatar
                      symbol={row.symbol}
                      name={row.name}
                      monogram={row.symbol.slice(0, 2)}
                      size={Math.min(28, Math.round(rect.h / 4))}
                    />
                  ) : null}
                  {labelled ? (
                    <span className={cn('font-semibold', roomy ? 'text-body-m' : 'text-body-xs')}>
                      {row.symbol}
                    </span>
                  ) : null}
                  {labelled ? (
                    <span className={roomy ? 'text-body-xs' : 'text-rail-tile'}>
                      {move === null ? '—' : formatChange(move)}
                    </span>
                  ) : null}
                </SymbolTarget>
              )
            }),
        )}
        {hover === null ? null : (
          <div
            aria-hidden="true"
            className="pointer-events-none absolute z-20 flex w-52 flex-col gap-1 border border-tooltip-border bg-tooltip px-3 py-2 text-tooltip-foreground shadow-(--shadow-chart-tip)"
            style={{
              left: Math.max(0, Math.min(hover.x + 14, width - 216)),
              top: Math.max(0, Math.min(hover.y + 16, height - 120)),
            }}
          >
            <p className="truncate text-h-s">
              {hover.row.symbol}{' '}
              <span className="text-body-xs font-normal text-muted-foreground-subtle">
                {hover.row.name}
              </span>
            </p>
            <TipRow label="Price">
              {price === undefined ? (
                '—'
              ) : (
                <MetricFigure metric={price} value={hover.row.values['ltp'] ?? null} />
              )}
            </TipRow>
            {colourMetric === undefined ? null : (
              <TipRow label={colourMetric.label}>
                <MetricFigure metric={colourMetric} value={hover.row.values[colourKey] ?? null} />
              </TipRow>
            )}
            <TipRow label="Market cap">
              {cap === undefined ? (
                '—'
              ) : (
                <MetricFigure metric={cap} value={hover.row.values['market_cap'] ?? null} />
              )}
            </TipRow>
          </div>
        )}
      </div>
    </div>
  )
}

function TipRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <p className="flex justify-between gap-3 text-body-xs">
      <span className="text-muted-foreground-subtle">{label}</span>
      <span className="font-semibold tnum">{children}</span>
    </p>
  )
}
